import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BrainOrb, BrainPrompt, Button, Card, GlassPill, Radar } from '../components/primitives';
import type { Command } from '../components/primitives';
import { PlayerDossier } from '../components/PlayerDossier';
import { useAuth } from '../contexts/AuthContext';
import { usePageCommands } from '../contexts/BrainBarContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { usePlayerRatings } from '../lib/queries/usePlayerRatings';
import { usePlayerVideoTags } from '../lib/queries/usePlayerVideoTags';
import { CoachFeedbackList } from '../components/CoachFeedbackList';
import { usePlayerPlan } from '../lib/queries/usePlayerPlan';
import { useVoiceToText } from '../lib/voiceInput';
import { supabase } from '../lib/supabase';
import { loadLocalPlayers } from '../lib/demoData';
import {
  PLAYER_GOAL_QUESTIONS,
  advancePlayerGoal,
  createInitialPlayerGoal,
  currentPlayerGoalQuestion,
  listPlayerGoals,
  savePlayerGoal,
} from '../lib/playerGoals';
import type { PlayerGoalState, PlayerGoalSummary } from '../lib/playerGoals';
import { saveCornerRatings, SUB_CORNERS_BY_KEY } from '../lib/rateCapture';
import type { CornerKey, RateScores } from '../lib/rateCapture';
import { saveVideoTag } from '../lib/videoTagCapture';
import { editPlayer, removePlayer } from '../lib/playerCrud';
import { useRegion } from '../lib/i18n/useRegion';
import { draftParentDigest, listPlayerParentDigests, saveParentDigest } from '../lib/parentDigest';
import type { ParentDigestSummary } from '../lib/parentDigest';
import { draftDevelopmentPlan, saveDevelopmentPlan } from '../lib/planComposer';
import type { DraftedPlan } from '../lib/planComposer';
import {
  draftPlayerDevelopmentAnalysis,
  fallbackPlayerDevelopmentAnalysis,
  listPlayerDevelopmentAnalyses,
  savePlayerDevelopmentAnalysis,
} from '../lib/playerDevelopmentAnalysis';
import type {
  PlayerDevelopmentAnalysis,
  PlayerDevelopmentAnalysisSummary,
} from '../lib/playerDevelopmentAnalysis';
import { toggleMilestoneMet } from '../lib/milestoneEditor';
import './page-shell.css';
import './PlayerDetail.css';

interface PlayerRow {
  id: string;
  name: string;
  position: string | null;
}

interface ConversationTurn {
  id: string;
  role: 'coach' | 'brain';
  text: string;
}

function relativeDate(iso: string): string {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.round(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
}

function velocityArrow(delta: number): string {
  if (delta > 4) return '↑';
  if (delta > 1) return '↗';
  if (delta < -4) return '↓';
  if (delta < -1) return '↘';
  return '·';
}

function factBody(fact: string): string {
  return fact.replace(/^Goal for[^\n]*\n/, '').replace(/\n/g, ' ');
}

export function PlayerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const { terms } = useRegion();
  const [player, setPlayer] = useState<PlayerRow | null>(null);
  const [ratingsRefreshKey, setRatingsRefreshKey] = useState(0);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  const [clipsRefreshKey, setClipsRefreshKey] = useState(0);
  const ratings = usePlayerRatings(id, ratingsRefreshKey);
  const clips = usePlayerVideoTags(id, 5, clipsRefreshKey);
  const plan = usePlayerPlan(id, planRefreshKey);
  const [goals, setGoals] = useState<PlayerGoalSummary[]>([]);
  const [flowOpen, setFlowOpen] = useState(false);
  const [state, setState] = useState<PlayerGoalState>(() => createInitialPlayerGoal());
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [scores, setScores] = useState<RateScores>({ tech: 50, phys: 50, psych: 50, social: 50 });
  const [expandedCorners, setExpandedCorners] = useState<Record<CornerKey, boolean>>({ tech: false, phys: false, psych: false, social: false });
  const [subScores, setSubScores] = useState<Record<string, number>>({});
  const [rateSaving, setRateSaving] = useState(false);
  const [rateMessage, setRateMessage] = useState<string | null>(null);
  const [digests, setDigests] = useState<ParentDigestSummary[]>([]);
  const [digestText, setDigestText] = useState('');
  const [digestDrafting, setDigestDrafting] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestMessage, setDigestMessage] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<PlayerDevelopmentAnalysisSummary[]>([]);
  const [analysisDraft, setAnalysisDraft] = useState<PlayerDevelopmentAnalysis | null>(null);
  const [analysisDrafting, setAnalysisDrafting] = useState(false);
  const [analysisSaving, setAnalysisSaving] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);
  const [planFocus, setPlanFocus] = useState('');
  const [planDrafting, setPlanDrafting] = useState(false);
  const [planDraft, setPlanDraft] = useState<DraftedPlan | null>(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [planMessage, setPlanMessage] = useState<string | null>(null);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagUrl, setTagUrl] = useState('');
  const [tagIn, setTagIn] = useState(0);
  const [tagOut, setTagOut] = useState(15);
  const [tagCorner, setTagCorner] = useState<CornerKey | null>(null);
  const [tagNotes, setTagNotes] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [tagMessage, setTagMessage] = useState<string | null>(null);
  // Player identity edit + remove state. Inline rename + roster removal
  // surfaced from a small toolbar under the player name.
  const navigate = useNavigate();
  const [identityEditing, setIdentityEditing] = useState(false);
  const [identityName, setIdentityName] = useState('');
  const [identityPosition, setIdentityPosition] = useState('');
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identityMessage, setIdentityMessage] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [removeSaving, setRemoveSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        // Player identity lives on `users`; team_players carries the
        // squad position. Look up the user, then their team_players row.
        // No standalone `players` table on the live DB.
        const { data: u, error } = await supabase
          .from('users')
          .select('id, name')
          .eq('id', id)
          .eq('role', 'PLAYER')
          .maybeSingle();
        if (cancelled) return;
        if (error) console.warn('[player] load failed', error.message);
        if (u) {
          const { data: tp } = await supabase
            .from('team_players')
            .select('position')
            .eq('player_id', id)
            .eq('is_active', true)
            .maybeSingle();
          if (cancelled) return;
          setPlayer({
            id: u.id as string,
            name: (u.name as string | undefined) ?? '',
            position: (tp?.position as string | null) ?? null,
          });
          return;
        }
        const local = loadLocalPlayers().find((p) => p.id === id);
        setPlayer(local ? { id: local.id, name: local.name, position: local.position } : null);
      } catch (err) {
        if (!cancelled) {
          console.warn('[player] threw', err);
          const local = loadLocalPlayers().find((p) => p.id === id);
          setPlayer(local ? { id: local.id, name: local.name, position: local.position } : null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const refreshGoals = useCallback(async () => {
    if (!user || !id) return;
    try {
      const list = await listPlayerGoals(user.id, id);
      setGoals(list);
    } catch (err) {
      console.warn('[player goals] threw', err);
      setGoals([]);
    }
  }, [user, id]);

  useEffect(() => { void refreshGoals(); }, [refreshGoals]);

  const refreshDigests = useCallback(async () => {
    if (!user || !id) return;
    try {
      const list = await listPlayerParentDigests(user.id, id);
      setDigests(list);
    } catch (err) {
      console.warn('[player digests] threw', err);
      setDigests([]);
    }
  }, [user, id]);

  useEffect(() => { void refreshDigests(); }, [refreshDigests]);

  const refreshAnalyses = useCallback(async () => {
    if (!user || !id) return;
    try {
      const list = await listPlayerDevelopmentAnalyses(user.id, id);
      setAnalyses(list);
    } catch (err) {
      console.warn('[player analyses] threw', err);
      setAnalyses([]);
    }
  }, [user, id]);

  useEffect(() => { void refreshAnalyses(); }, [refreshAnalyses]);

  const draftDigest = useCallback(async () => {
    if (digestDrafting) return;
    if (!user || !id || !player) return;
    setDigestDrafting(true);
    setDigestMessage(null);
    try {
      const goalFacts = goals.map((g) => factBody(g.fact));
      const text = await draftParentDigest({
        playerName: player.name,
        position: player.position,
        ratings: ratings.values,
        ratingsCount: ratings.count,
        goals: goalFacts,
      });
      setDigestText(text ?? '');
      if (!text) setDigestMessage('Brain unreachable — type your own draft below.');
    } catch (err) {
      setDigestMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setDigestDrafting(false);
    }
  }, [digestDrafting, user, id, player, goals, ratings]);

  const runPlayerAnalysis = useCallback(async () => {
    if (analysisDrafting) return;
    if (!id || !player) return;
    setAnalysisDrafting(true);
    setAnalysisMessage(null);
    const goalFacts = goals.map((g) => factBody(g.fact));
    const planMilestones = plan?.milestones.map((m) => m.text).filter((text) => text.trim()) ?? [];
    const clipNotes = clips.map((clip) => clip.notes ?? '').filter((text) => text.trim());
    const options = {
      playerId: id,
      playerName: player.name,
      position: player.position,
      ratings: ratings.values,
      velocity: ratings.velocity,
      ratingsCount: ratings.count,
      goals: goalFacts,
      planMilestones,
      clipNotes,
    };
    try {
      const drafted = await draftPlayerDevelopmentAnalysis(options);
      if (drafted) {
        setAnalysisDraft(drafted);
      } else {
        setAnalysisDraft(fallbackPlayerDevelopmentAnalysis(options));
        setAnalysisMessage('Brain unreachable — showing a data-based fallback.');
      }
    } catch (err) {
      console.warn('[player analysis] draft threw', err);
      setAnalysisDraft(fallbackPlayerDevelopmentAnalysis(options));
      setAnalysisMessage('Brain unreachable — showing a data-based fallback.');
    } finally {
      setAnalysisDrafting(false);
    }
  }, [analysisDrafting, id, player, goals, plan, clips, ratings]);

  const playerCommands = useMemo<Command[]>(
    () => player ? [{
      family: 'ask',
      label: 'Analyse this player',
      hint: 'trajectory + 3 actions',
      onRun: () => void runPlayerAnalysis(),
    }] : [],
    [player, runPlayerAnalysis],
  );
  usePageCommands(playerCommands);

  const draftPlan = useCallback(async () => {
    if (planDrafting) return;
    if (!planFocus.trim()) {
      setPlanMessage('Add a focus theme first.');
      return;
    }
    if (!user || !id || !player) return;
    setPlanDrafting(true);
    setPlanMessage(null);
    try {
      const goalFacts = goals.map((g) => factBody(g.fact));
      const drafted = await draftDevelopmentPlan({
        playerName: player.name,
        position: player.position,
        focusTheme: planFocus.trim(),
        ratings: ratings.values,
        ratingsCount: ratings.count,
        goalFacts,
      });
      if (!drafted) {
        setPlanMessage('Brain unreachable — can\'t draft right now.');
      } else {
        setPlanDraft(drafted);
      }
    } catch (err) {
      setPlanMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setPlanDrafting(false);
    }
  }, [planDrafting, user, id, player, planFocus, goals, ratings]);

  const commitAnalysis = useCallback(async () => {
    if (analysisSaving) return;
    if (!user || !id || !player || !analysisDraft) return;
    setAnalysisSaving(true);
    const result = await savePlayerDevelopmentAnalysis({
      coachId: user.id,
      teamId: team?.id ?? null,
      playerId: id,
      playerName: player.name,
      ratings: ratings.values,
      velocity: ratings.velocity,
      analysis: analysisDraft,
    });
    setAnalysisSaving(false);
    if (result.ok) {
      setAnalysisDraft(null);
      setAnalysisMessage('Saved.');
      void refreshAnalyses();
    } else {
      setAnalysisMessage(result.error);
    }
  }, [analysisSaving, user, id, player, analysisDraft, team, ratings, refreshAnalyses]);

  const commitPlan = useCallback(async () => {
    if (planSaving) return;
    if (!user || !id || !planDraft) return;
    setPlanSaving(true);
    const result = await saveDevelopmentPlan({
      coachId: user.id,
      playerId: id,
      plan: planDraft,
    });
    setPlanSaving(false);
    if (result.ok) {
      setPlanDraft(null);
      setPlanFocus('');
      setPlanMessage('Plan saved.');
      setPlanRefreshKey((k) => k + 1);
    } else {
      setPlanMessage(result.error);
    }
  }, [planSaving, user, id, planDraft]);

  const commitDigest = useCallback(async () => {
    if (digestSaving) return;
    if (!digestText.trim()) {
      setDigestMessage('Write or draft an update first.');
      return;
    }
    if (!user || !id || !player) return;
    setDigestSaving(true);
    const result = await saveParentDigest({
      coachId: user.id,
      teamId: team?.id ?? null,
      playerId: id,
      playerName: player.name,
      text: digestText.trim(),
    });
    setDigestSaving(false);
    if (result.ok) {
      setDigestText('');
      setDigestMessage('Saved.');
      void refreshDigests();
    } else {
      setDigestMessage(result.error);
    }
  }, [digestSaving, user, id, player, team, digestText, refreshDigests]);

  const beginFlow = () => {
    setFlowOpen(true);
    setState(createInitialPlayerGoal());
    setSaveMessage(null);
    setTurns([{ id: 'first', role: 'brain', text: PLAYER_GOAL_QUESTIONS[0].prompt }]);
  };

  const cancelFlow = () => {
    setFlowOpen(false);
    setState(createInitialPlayerGoal());
    setTurns([]);
  };

  const submit = useCallback(async (text: string) => {
    const clean = text.trim();
    if (!clean || !user || !id || !player) return;
    const next = advancePlayerGoal(state, clean);
    setState(next);
    setTurns((existing) => {
      const withCoach = [...existing, { id: crypto.randomUUID(), role: 'coach' as const, text: clean }];
      const nextQ = currentPlayerGoalQuestion(next);
      if (nextQ) return [...withCoach, { id: crypto.randomUUID(), role: 'brain' as const, text: nextQ.prompt }];
      return [...withCoach, {
        id: crypto.randomUUID(),
        role: 'brain' as const,
        text: 'Saved. Brain will weigh this on every turn that touches this player.',
      }];
    });

    if (next.finished) {
      const result = await savePlayerGoal({
        coachId: user.id,
        teamId: team?.id ?? null,
        playerId: id,
        playerName: player.name,
        answers: next.answers,
      });
      if (result.ok) {
        setSaveMessage('Saved.');
        void refreshGoals();
      } else {
        setSaveMessage(result.error);
      }
    }
  }, [state, user, id, team, player, refreshGoals]);

  const voice = useVoiceToText({ onTranscript: submit });

  const corners: Array<{ key: keyof typeof ratings.values; label: string; tag: string }> = [
    { key: 'tech', label: 'Technical', tag: 'tech' },
    { key: 'phys', label: 'Physical', tag: 'phys' },
    { key: 'psych', label: 'Psychological', tag: 'psych' },
    { key: 'social', label: 'Social', tag: 'social' },
  ];

  return (
    <main className="main page player-detail">
      <header className="page-head">
        <Link to="/players" className="page-back">← Players</Link>
        {!identityEditing ? (
          <>
            <h1 className="page-title">{player?.name ?? 'Player'}</h1>
            {player?.position && <p className="page-sub">{player.position}</p>}
            {player && (
              <div className="player-identity-tools">
                <button
                  type="button"
                  className="player-identity-tools__btn"
                  onClick={() => {
                    setIdentityName(player.name);
                    setIdentityPosition(player.position ?? '');
                    setIdentityMessage(null);
                    setIdentityEditing(true);
                  }}
                  aria-label="Edit player name and position"
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="player-identity-tools__btn player-identity-tools__btn--danger"
                  onClick={() => setRemoveConfirm(true)}
                  aria-label={`Remove player from ${terms.roster}`}
                >
                  Remove from {terms.roster}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="players-add players-add--inline">
            <div className="players-add__row">
              <label className="players-add__field">
                <span>Name</span>
                <input
                  type="text"
                  value={identityName}
                  onChange={(e) => setIdentityName(e.target.value)}
                  aria-label="Player name"
                  autoFocus
                />
              </label>
              <label className="players-add__field players-add__field--pos">
                <span>Position</span>
                <input
                  type="text"
                  value={identityPosition}
                  onChange={(e) => setIdentityPosition(e.target.value)}
                  aria-label="Player position"
                  maxLength={16}
                />
              </label>
              <Button
                variant="primary"
                size="touchline"
                onClick={async () => {
                  if (!player || identitySaving) return;
                  setIdentitySaving(true);
                  setIdentityMessage(null);
                  const result = await editPlayer({
                    playerId: player.id,
                    name: identityName,
                    position: identityPosition || null,
                  });
                  setIdentitySaving(false);
                  if (!result.ok) {
                    setIdentityMessage(result.error);
                    return;
                  }
                  setPlayer({
                    ...player,
                    name: identityName.trim(),
                    position: identityPosition.trim() || null,
                  });
                  setIdentityEditing(false);
                }}
              >
                {identitySaving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="glass"
                size="touchline"
                onClick={() => { setIdentityEditing(false); setIdentityMessage(null); }}
              >
                Cancel
              </Button>
            </div>
            {identityMessage && (
              <p className="players-add__msg">{identityMessage}</p>
            )}
          </div>
        )}
        {removeConfirm && player && (
          <div className="player-remove-confirm" role="alertdialog" aria-label="Remove player">
            <p>
              Remove <strong>{player.name}</strong> from the {terms.roster}?
              {' '}On a real backend this is a soft-remove — historical ratings + goals stay attached.
            </p>
            <div className="player-remove-confirm__actions">
              <Button
                variant="glass"
                size="touchline"
                onClick={() => setRemoveConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="touchline"
                onClick={async () => {
                  if (!player || removeSaving) return;
                  setRemoveSaving(true);
                  const result = await removePlayer(player.id);
                  setRemoveSaving(false);
                  if (!result.ok) {
                    setIdentityMessage(result.error);
                    setRemoveConfirm(false);
                    return;
                  }
                  navigate('/players');
                }}
              >
                {removeSaving ? 'Removing…' : 'Remove player'}
              </Button>
            </div>
          </div>
        )}
      </header>

      <Card variant="glass" className="player-radar-card">
        <div className="player-radar-card__head">
          <div>
            <div className="page-section-label">Four-corner profile</div>
            <span className="player-radar-card__count">
              {ratings.count > 0 ? `${ratings.count} ratings · last 28 days` : 'awaiting first rating'}
            </span>
          </div>
          {!rateOpen && (
            <Button variant="glass" size="touchline" onClick={() => { setRateOpen(true); setRateMessage(null); }}>
              {ratings.count > 0 ? 'Rate again' : 'Rate now'}
            </Button>
          )}
        </div>
        <div className="player-radar-card__body">
          <Radar values={ratings.values} size={200} />
          <ul className="player-radar-card__legend">
            {corners.map((c) => (
              <li key={c.key} data-corner={c.tag}>
                <span className="player-radar-card__dot" data-corner={c.tag} />
                <span className="player-radar-card__label">{c.label}</span>
                <span className="player-radar-card__score num">{ratings.values[c.key]}</span>
                <span className="player-radar-card__velocity" data-direction={ratings.velocity[c.key] > 0 ? 'up' : ratings.velocity[c.key] < 0 ? 'down' : 'flat'}>
                  {velocityArrow(ratings.velocity[c.key])}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {ratings.count === 0 && !rateOpen && (
          <p className="page-empty">Ratings populate once recorded — radar shows zero until then.</p>
        )}
        {rateOpen && user && id && (
          <div className="rate-form">
            <div className="rate-form__label">Score each corner 0–100. The radar updates as soon as you save.</div>
            {(['tech', 'phys', 'psych', 'social'] as const).map((key) => {
              const cornerLabel = { tech: 'Technical', phys: 'Physical', psych: 'Psychological', social: 'Social' }[key];
              const expanded = expandedCorners[key];
              return (
                <div key={key} className="rate-form__corner-block">
                  <label className="rate-form__row">
                    <span className="rate-form__corner" data-corner={key}>{cornerLabel}</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={scores[key]}
                      onChange={(e) => setScores((s) => ({ ...s, [key]: Number(e.target.value) }))}
                      className="rate-form__slider"
                      data-corner={key}
                      aria-label={`${cornerLabel} score, 0 to 100`}
                      aria-valuetext={`${cornerLabel} ${scores[key]}`}
                    />
                    <span className="rate-form__value num" aria-hidden>{scores[key]}</span>
                  </label>
                  <button
                    type="button"
                    className="rate-form__expand"
                    aria-expanded={expanded}
                    onClick={() => {
                      setExpandedCorners((prev) => ({ ...prev, [key]: !prev[key] }));
                      if (!expanded) {
                        setSubScores((prev) => {
                          const next = { ...prev };
                          for (const sub of SUB_CORNERS_BY_KEY[key]) {
                            if (next[sub.id] == null) next[sub.id] = scores[key];
                          }
                          return next;
                        });
                      }
                    }}
                  >
                    {expanded ? '− hide details' : '+ rate sub-corners'}
                  </button>
                  {expanded && (
                    <div className="rate-form__subs">
                      {SUB_CORNERS_BY_KEY[key].map((sub) => (
                        <label key={sub.id} className="rate-form__sub-row">
                          <span className="rate-form__sub-label">{sub.label}</span>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={subScores[sub.id] ?? scores[key]}
                            onChange={(e) =>
                              setSubScores((prev) => ({ ...prev, [sub.id]: Number(e.target.value) }))
                            }
                            className="rate-form__slider rate-form__slider--sub"
                            data-corner={key}
                            aria-label={`${cornerLabel} sub-corner ${sub.label}, 0 to 100`}
                            aria-valuetext={`${sub.label} ${subScores[sub.id] ?? scores[key]}`}
                          />
                          <span className="rate-form__value num" aria-hidden>{subScores[sub.id] ?? scores[key]}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="rate-form__actions">
              <Button
                variant="primary"
                size="touchline"
                onClick={async () => {
                  if (rateSaving) return;
                  setRateSaving(true);
                  setRateMessage(null);
                  const expandedSubs: Record<string, number> = {};
                  (Object.keys(expandedCorners) as CornerKey[]).forEach((k) => {
                    if (!expandedCorners[k]) return;
                    for (const sub of SUB_CORNERS_BY_KEY[k]) {
                      expandedSubs[sub.id] = subScores[sub.id] ?? scores[k];
                    }
                  });
                  const result = await saveCornerRatings({
                    coachId: user.id,
                    playerId: id,
                    scores,
                    subScores: expandedSubs,
                  });
                  setRateSaving(false);
                  if (result.ok) {
                    setRateMessage('Saved.');
                    setRateOpen(false);
                    setExpandedCorners({ tech: false, phys: false, psych: false, social: false });
                    setSubScores({});
                    setRatingsRefreshKey((k) => k + 1);
                  } else {
                    setRateMessage(result.error);
                  }
                }}
              >
                {rateSaving ? 'Saving…' : 'Save ratings'}
              </Button>
              <Button variant="glass" size="touchline" onClick={() => setRateOpen(false)}>
                Cancel
              </Button>
              {rateMessage && <span className="rate-form__msg">{rateMessage}</span>}
            </div>
          </div>
        )}
      </Card>

      {id && (
        <PlayerDossier playerId={id} playerName={player?.name ?? 'this player'} />
      )}

      <Card variant="glass" className="player-plan">
        <div className="player-plan__head">
          <div className="page-section-label">{plan ? 'Active development plan' : 'Development plan'}</div>
          {!plan && !planDraft && (
            <span className="player-plan__hint">No active plan. Draft a four-week cycle below.</span>
          )}
        </div>
        {plan && (
          <>
            <div className="player-plan__period">
              {new Date(plan.period_start).toLocaleDateString()} → {new Date(plan.period_end).toLocaleDateString()}
              {plan.review_date && <> · review {new Date(plan.review_date).toLocaleDateString()}</>}
            </div>
            {plan.milestones.length > 0 && (
              <ul className="player-plan__milestones">
                {plan.milestones.map((m, i) => (
                  <li key={i} data-met={!!m.met_at || undefined}>
                    <button
                      type="button"
                      className="player-plan__check player-plan__check--button"
                      aria-label={m.met_at ? 'Mark as not met' : 'Mark as met'}
                      onClick={async () => {
                        const result = await toggleMilestoneMet(plan.id, i);
                        if (result.ok) setPlanRefreshKey((k) => k + 1);
                        else setPlanMessage(result.error);
                      }}
                    >
                      {m.met_at ? '✓' : '·'}
                    </button>
                    <span>{m.text}</span>
                    {m.due_date && <span className="player-plan__due">{new Date(m.due_date).toLocaleDateString()}</span>}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {!plan && !planDraft && (
          <div className="player-plan__compose">
            <input
              type="text"
              className="player-plan__input"
              placeholder="Focus theme — e.g. ‘composure under press in tight spaces’"
              aria-label="Plan focus theme"
              value={planFocus}
              onChange={(e) => setPlanFocus(e.target.value)}
            />
            <Button variant="primary" size="touchline" onClick={() => void draftPlan()}>
              {planDrafting ? 'Brain drafting…' : 'Draft a 4-week plan'}
            </Button>
          </div>
        )}
        {planDraft && (
          <div className="player-plan__draft">
            <span className="player-plan__draft-eyebrow">Draft · {planDraft.primary_corner.toUpperCase()}</span>
            <p className="player-plan__draft-rationale">{planDraft.rationale}</p>
            <ul className="player-plan__milestones">
              {planDraft.milestones.map((m, i) => (
                <li key={i}>
                  <span className="player-plan__check">·</span>
                  <span>{m.text}</span>
                  <span className="player-plan__due">{m.due_date}</span>
                </li>
              ))}
            </ul>
            <div className="player-plan__draft-actions">
              <Button variant="primary" size="touchline" onClick={() => void commitPlan()}>
                {planSaving ? 'Saving…' : 'Save plan'}
              </Button>
              <Button variant="glass" size="touchline" onClick={() => setPlanDraft(null)}>Refine</Button>
            </div>
          </div>
        )}
        {planMessage && <p className="player-plan__msg">{planMessage}</p>}
      </Card>

      {(analysisDraft || analysisDrafting || analysisMessage || analyses.length > 0) && (
        <section className="player-analysis" aria-label="Brain development insight">
          <div className="player-analysis__head">
            <div>
              <span className="page-eyebrow">Brain insight</span>
              <h2 className="player-analysis__title">Development trajectory</h2>
            </div>
            {analysisDrafting && <GlassPill mono>drafting…</GlassPill>}
          </div>

          {analysisDraft && (
            <Card variant="opaque" className="player-analysis__draft">
              <p className="player-analysis__summary">{analysisDraft.summary}</p>
              <ol className="player-analysis__actions">
                {analysisDraft.actions.map((action, i) => (
                  <li key={`${action.corner}-${i}`}>
                    <span className="player-analysis__corner">{action.corner.toUpperCase()}</span>
                    <span>{action.action}</span>
                    <small>{action.rationale}</small>
                  </li>
                ))}
              </ol>
              <p className="player-analysis__parent">{analysisDraft.parent_highlight}</p>
              <div className="player-analysis__draft-actions">
                <Button variant="primary" size="touchline" onClick={() => void commitAnalysis()}>
                  {analysisSaving ? 'Saving…' : 'Save insight'}
                </Button>
                <Button variant="glass" size="touchline" onClick={() => setAnalysisDraft(null)}>
                  Discard
                </Button>
              </div>
            </Card>
          )}

          {analysisMessage && <p className="player-analysis__msg">{analysisMessage}</p>}

          {analyses.length > 0 && (
            <ul className="player-analysis__list">
              {analyses.map((item) => (
                <li key={item.id}>
                  <Card variant="opaque">
                    <div className="player-analysis__when">{relativeDate(item.created_at)}</div>
                    <pre className="player-analysis__fact">{item.fact}</pre>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="player-clips player-portfolio-target" aria-label="Clips">
        <div className="player-clips__head">
          <div className="page-section-label">Clips · {clips.length}</div>
          {!tagOpen && (
            <Button variant="glass" size="touchline" onClick={() => { setTagOpen(true); setTagMessage(null); }}>
              Tag a clip
            </Button>
          )}
          {clips.length > 0 && (
            <Button
              variant="glass"
              size="touchline"
              onClick={() => window.print()}
              title="Print or save as PDF — recruiting-portfolio shape with player + clips + ratings"
            >
              Export portfolio
            </Button>
          )}
        </div>

        {tagOpen && user && id && (
          <Card variant="glass" className="tag-form">
            <input
              type="url"
              className="tag-form__url"
              placeholder="paste a YouTube / Veo / Hudl URL"
              aria-label="Clip URL"
              value={tagUrl}
              onChange={(e) => setTagUrl(e.target.value)}
              autoFocus
            />
            <div className="tag-form__row">
              <label className="tag-form__field">
                <span>Start</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={tagIn}
                  onChange={(e) => setTagIn(Number(e.target.value))}
                  aria-label="Clip start time in seconds"
                />
                <span className="tag-form__unit" aria-hidden>s</span>
              </label>
              <label className="tag-form__field">
                <span>End</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={tagOut}
                  onChange={(e) => setTagOut(Number(e.target.value))}
                  aria-label="Clip end time in seconds"
                />
                <span className="tag-form__unit" aria-hidden>s</span>
              </label>
              <div className="tag-form__corners" role="group" aria-label="Tag clip with a corner">
                {(['tech', 'phys', 'psych', 'social'] as const).map((c) => {
                  const cornerLabel = { tech: 'Technical', phys: 'Physical', psych: 'Psychological', social: 'Social' }[c];
                  return (
                    <button
                      key={c}
                      type="button"
                      className="tag-form__corner-chip"
                      data-corner={c}
                      data-active={tagCorner === c || undefined}
                      aria-pressed={tagCorner === c}
                      aria-label={`${cornerLabel} corner`}
                      onClick={() => setTagCorner((cur) => (cur === c ? null : c))}
                    >
                      {{ tech: 'TECH', phys: 'PHYS', psych: 'PSYCH', social: 'SOCIAL' }[c]}
                    </button>
                  );
                })}
              </div>
            </div>
            <textarea
              className="tag-form__notes"
              placeholder="what to look for in this clip"
              aria-label="Coaching note for this clip"
              value={tagNotes}
              onChange={(e) => setTagNotes(e.target.value)}
              rows={2}
            />
            <div className="tag-form__actions">
              <Button
                variant="primary"
                size="touchline"
                onClick={async () => {
                  if (tagSaving) return;
                  if (!tagUrl.trim()) {
                    setTagMessage('Paste a clip URL first.');
                    return;
                  }
                  if (tagOut <= tagIn) {
                    setTagMessage('End time must be after start time.');
                    return;
                  }
                  setTagSaving(true);
                  const result = await saveVideoTag({
                    coachId: user.id, playerId: id,
                    clipUrl: tagUrl, tsIn: tagIn, tsOut: tagOut,
                    corner: tagCorner, notes: tagNotes,
                  });
                  setTagSaving(false);
                  if (result.ok) {
                    setTagUrl(''); setTagIn(0); setTagOut(15); setTagCorner(null); setTagNotes('');
                    setTagOpen(false);
                    setTagMessage('Saved.');
                    setClipsRefreshKey((k) => k + 1);
                  } else {
                    setTagMessage(result.error);
                  }
                }}
              >
                {tagSaving ? 'Saving…' : 'Save tag'}
              </Button>
              <Button variant="glass" size="touchline" onClick={() => setTagOpen(false)}>Cancel</Button>
              {tagMessage && <span className="tag-form__msg">{tagMessage}</span>}
            </div>
          </Card>
        )}

        {clips.length === 0 && !tagOpen && (
          <Card variant="opaque"><p className="page-empty">No clips yet — tag the first one above.</p></Card>
        )}

        {clips.length > 0 && (
          <ul className="player-clips__list">
            {clips.map((clip) => (
              <li key={clip.id}>
                <Card variant="opaque">
                  <div className="player-clips__when">{relativeDate(clip.created_at)}</div>
                  {clip.notes && <p className="player-clips__notes">{clip.notes}</p>}
                  <div className="player-clips__meta">
                    {clip.platform && <GlassPill mono>{clip.platform.toLowerCase()}</GlassPill>}
                    {clip.ts_in != null && clip.ts_out != null && (
                      <GlassPill mono>{Math.round(clip.ts_out - clip.ts_in)}s</GlassPill>
                    )}
                    <a href={clip.clip_url} target="_blank" rel="noreferrer" className="player-clips__open">open ↗</a>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
      {tagMessage && !tagOpen && <p className="tag-form__msg">{tagMessage}</p>}

      <section className="player-digest" aria-label="Parent digest">
        <div className="player-digest__head">
          <div>
            <span className="page-eyebrow">Parent digest</span>
            <h2 className="player-digest__title">{digests.length === 0 ? 'No update drafted yet.' : `${digests.length} on file`}</h2>
            <p className="page-sub">
              Brain drafts a 2–3 sentence fortnightly summary. You edit, you save. Delivery to parents lands later.
            </p>
          </div>
          <Button variant="primary" size="touchline" onClick={() => void draftDigest()}>
            {digestDrafting ? 'Brain drafting…' : 'Draft a parent update'}
          </Button>
        </div>

        {(digestText || digestMessage) && (
          <Card variant="glass" className="player-digest__draft">
            <textarea
              className="player-digest__textarea"
              value={digestText}
              onChange={(e) => setDigestText(e.target.value)}
              placeholder="Brain draft will land here. You can edit before saving."
              rows={4}
            />
            <div className="player-digest__actions">
              <Button variant="primary" size="touchline" onClick={() => void commitDigest()}>
                {digestSaving ? 'Saving…' : 'Save digest'}
              </Button>
              <Button variant="glass" size="touchline" onClick={() => { setDigestText(''); setDigestMessage(null); }}>
                Discard
              </Button>
              {digestMessage && <span className="player-digest__msg">{digestMessage}</span>}
            </div>
          </Card>
        )}

        {digests.length > 0 && (
          <ul className="player-digest__list">
            {digests.map((d) => (
              <li key={d.id}>
                <Card variant="opaque">
                  <div className="player-digest__when">{relativeDate(d.created_at)}</div>
                  <pre className="player-digest__fact">{d.fact}</pre>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="player-goals" aria-label="Active goals">
        <div className="player-goals__head">
          <div>
            <span className="page-eyebrow">Active goals</span>
            <h2 className="player-goals__title">{goals.length === 0 ? 'No goals yet.' : `${goals.length} active`}</h2>
          </div>
          {!flowOpen && (
            <Button variant="primary" size="touchline" onClick={beginFlow}>
              {goals.length === 0 ? 'Set the first goal' : 'Add another goal'}
            </Button>
          )}
        </div>

        {flowOpen && (
          <Card variant="glass" className="player-goal-flow">
            <div className="player-goal-flow__head">
              <BrainOrb size="md" state={voice.listening ? 'listening' : 'idle'} ariaLabel="Brain" />
              <GlassPill mono>{Math.min(state.step + 1, PLAYER_GOAL_QUESTIONS.length)} of {PLAYER_GOAL_QUESTIONS.length}</GlassPill>
            </div>

            <div className="player-goal-flow__thread" aria-live="polite">
              {turns.map((turn) => (
                <div key={turn.id} className={`player-goal-flow__turn player-goal-flow__turn--${turn.role}`}>
                  <span className="player-goal-flow__turn-speaker">
                    {turn.role === 'brain' && <BrainOrb size="xs" ariaLabel="Brain" />}
                    {turn.role === 'brain' ? 'Brain' : 'Coach'}
                  </span>
                  <p>{turn.text}</p>
                </div>
              ))}
            </div>

            {!state.finished && (
              <BrainPrompt
                hint="hold the orb or type"
                placeholder="say what comes to mind"
                listening={voice.listening}
                voiceStatus={voice.status}
                onMicStart={() => void voice.start()}
                onMicStop={voice.stop}
                onSubmit={submit}
              />
            )}

            {state.finished && (
              <div className="player-goal-flow__done">
                <Button variant="primary" size="touchline" onClick={cancelFlow}>Done</Button>
                <Button variant="glass" size="touchline" onClick={beginFlow}>Add another</Button>
              </div>
            )}

            {!state.finished && (
              <button type="button" className="player-goal-flow__cancel" onClick={cancelFlow}>cancel</button>
            )}

            {saveMessage && <p className="player-goal-flow__status">{saveMessage}</p>}
          </Card>
        )}

        {goals.length > 0 && (
          <ul className="player-goals__list">
            {goals.map((goal) => (
              <li key={goal.id}>
                <Card variant="opaque">
                  <div className="player-goals__when">{relativeDate(goal.created_at)}</div>
                  <pre className="player-goals__fact">{goal.fact}</pre>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {id && <CoachFeedbackList playerId={id} />}
    </main>
  );
}
